import * as messagesApi from '@/services/apiServices/messagesService';
import { showNotification } from '@/plugins/notification';

/**
 * Vuex actions for the "messages" module.
 *
 * Implements the DM/group-chat messaging flows: loading and paginating the
 * conversation list and "Search People" results, starting/creating direct
 * and group conversations, loading and paginating per-conversation message
 * history, sending/retrying/editing/deleting messages (optimistic, with
 * rollback on failure), typing indicators, read/unread state, and DM
 * settings. Talks to the REST endpoints and realtime sockets wrapped by
 * messagesService.js (messagesApi); several socket handlers here treat
 * frames as unconfirmed-shape "something changed" signals and simply
 * refetch from REST rather than parse the frame payload directly. Also
 * hosts a handful of module-local helpers (below) for conversation-preview
 * text/icon derivation and debounced realtime refreshes.
 */

// Text is intentionally plain (no emoji) — the conversation list renders a
// theme-aware SVG icon (see ConversationList.vue's PREVIEW_ICON_MAP, driven
// by conversationPreviewIcon below) next to this label instead, since emoji
// glyphs render with their own fixed colors on every platform and can't be
// recolored for dark theme.
const ATTACHMENT_PREVIEW_LABELS = {
  image: 'Photo',
  video: 'Video',
  audio: 'Audio',
  zip: 'Zip',
};

// Conversation-list preview text — mirrors WhatsApp's "Photo" / "Video" /
// "Audio" / "File" fallback for attachment-only messages with no caption.
// Same "You deleted this message" vs "This message was deleted" split
// MessageBubble.vue's own bubble placeholder uses (and messagesApi.js's
// conversationPreviewFromLastMessage mirrors for the server-fetched list) —
// this is the separate local/optimistic path exercised right when a message
// is deleted while its conversation is already open, so it needs the same
// check independently rather than falling back to the generic text.
function conversationPreviewText(message) {
  if (message.isDeleted) {
    const deletedByMe = message.senderId === messagesApi.getCurrentUser().id;
    return deletedByMe ? 'You deleted this message' : 'This message was deleted';
  }
  if (message.text) return message.text;
  const attachment = message.attachments?.[0];
  if (attachment) return ATTACHMENT_PREVIEW_LABELS[attachment.type] || 'File';
  if (message.linkPreview) return message.linkPreview.url;
  return '';
}

// Companion to conversationPreviewText — the small thumbnail shown next to
// "Photo" in the conversation list. Videos don't get one (no cheap client-side
// frame grab), only images.
function conversationPreviewThumbnail(message) {
  const attachment = message.attachments?.[0];
  return attachment?.type === 'image' ? attachment.url : '';
}

// Which icon (PREVIEW_ICON_MAP key) the conversation list should show before
// the preview text — set whenever the last message carries an attachment,
// independent of whether it also has a caption (mirrors conversationPreviewThumbnail).
function conversationPreviewIcon(message) {
  if (message.isDeleted) return 'deleted';
  return message.attachments?.[0]?.type || null;
}

// Whether the conversation list should show a link icon before the preview
// text — true only when the last message's preview text *is* its link
// (no caption/attachment ahead of it in conversationPreviewText's checks).
function conversationPreviewIsLink(message) {
  return Boolean(message.linkPreview && !message.attachments?.length);
}

// Shared by createGroup and openGroupChatForOrg — both create a fresh
// messaging-channel group and open it the same way startConversationWithPerson
// does for a DM (no message history yet, so seed an empty page directly
// instead of round-tripping through fetchMessages). An org-backed group
// (organizationId set — openGroupChatForOrg) creates its channel via the
// generic POST /api/messaging_channel (createOrgGroupConversation) instead
// of the dedicated .../create_group_conversations endpoint a manually
// created ("New Group") chat uses — the backend only accepts an
// organization_id-linked group through the generic endpoint; routing it
// through create_group_conversations instead returns a 400.
async function createAndOpenGroup(commit, name, participantIds, organizationId) {
  const conversation = organizationId
    ? await messagesApi.createOrgGroupConversation({ name, participantIds, organizationId })
    : await messagesApi.createGroupConversation({ name, participantIds });
  commit('upsertConversation', conversation);
  commit('setMessages', { conversationId: conversation.id, messages: [] });
  commit('setMessagesMeta', { conversationId: conversation.id, skip: 0, hasMore: false });
  commit('setActiveConversationId', conversation.id);
  return conversation.id;
}

// ws_conversations has no confirmed payload shape (see messagesApi.js) — its
// stated purpose is just "refresh the list on new messages", so rather than
// guess at mapping an unknown frame into a conversation object, treat any
// frame as a signal to refetch from the REST endpoints that are already
// known-good. Debounced since several frames can arrive in a short burst
// (e.g. a group message notifying every member at once).
// Presence stopgap: ws_presence never actually pushes anything (confirmed
// via DevTools — it connects, but zero incoming frames even during real
// activity), and conversations/list's own is_online field doesn't update
// live either — it's only ever as fresh as whatever fetchConversations last
// caught. Until real presence push is confirmed working end-to-end, poll on
// an interval so online/offline status self-corrects within ~20s instead of
// requiring a manual reload or waiting on unrelated message activity to
// happen to trigger a refetch.
const PRESENCE_POLL_INTERVAL_MS = 20000;

let conversationsRefreshTimer = null;
function scheduleConversationsRefresh(dispatch) {
  if (conversationsRefreshTimer) clearTimeout(conversationsRefreshTimer);
  conversationsRefreshTimer = setTimeout(() => {
    conversationsRefreshTimer = null;
    dispatch('fetchConversations');
    dispatch('fetchUnreadMessagesCount');
  }, 300);
}

// The 20s presence poll used to run for the app's entire lifetime (started
// once from initMessages at App.vue's boot), which meant the full
// conversations/list fetch (participants, previews, membership, etc. — not
// just a lightweight unread count) kept firing every 20s no matter what page
// was open, not only while the Messages section itself was visible. Scoped
// here instead, started/stopped by Messages/index.vue's own mount/unmount.
let conversationsPollTimer = null;

// ws_channels has the same unconfirmed-payload-shape situation as
// ws_conversations above (see messagesApi.js) — same signal-only treatment:
// refetch the channel's newest page and merge it in, rather than trust an
// unconfirmed frame shape enough to append it directly. Debounced for the
// same reason (a burst of frames shouldn't cause a refetch per frame).
let messagesRefreshTimer = null;
function scheduleMessagesRefresh(dispatch, conversationId) {
  if (messagesRefreshTimer) clearTimeout(messagesRefreshTimer);
  messagesRefreshTimer = setTimeout(() => {
    messagesRefreshTimer = null;
    dispatch('refreshActiveMessages', conversationId);
  }, 300);
}

// Populated with a conversationId for as long as THIS client's own
// sendMessage/retryMessage POST to /api/messages is in flight for it (see
// the try/finally around that call below). The server broadcasts its
// ws_conversations/ws_channels signals for a new message as soon as it's
// created — which can land back on this same socket connection and fire
// scheduleConversationsRefresh/scheduleMessagesRefresh BEFORE our own POST's
// HTTP response even comes back, racing the explicit, sequenced-after-response
// refreshListsAfterSend call below with a second, earlier, unsequenced one.
// The onConversationsRefresh/onMessagesRefresh handlers in initMessages
// check this set and skip scheduling while it's true for the relevant
// conversation, so the only refresh that actually fires for our own send is
// the one explicitly sequenced after the POST resolves.
const sendInFlightConversationIds = new Set();

// After POST /api/messages confirms a send (sendMessage/retryMessage below),
// re-sync every list the server-broadcast realtime signals would otherwise
// have refreshed instead of trusting only the optimistic local commits
// above: refreshActiveMessages re-fetches this conversation's own message
// page (GET .../messages/list), fetchConversations re-fetches the
// conversation list (GET .../conversations/list), and fetchUnreadMessagesCount
// re-fetches the Leftbar badge (GET .../unread-count) — the same three the
// ws_conversations/ws_channels-triggered refresh would have fired, just
// guaranteed to run after this response instead of racing it.
// conversationType narrows fetchConversations to just the side (`user`/`group`)
// this message's own conversation belongs to — sending into a DM has no
// reason to also re-fetch the entire group list, and vice versa.
function refreshListsAfterSend(dispatch, conversationId, conversationType) {
  dispatch('refreshActiveMessages', conversationId);
  dispatch('fetchConversations', { types: [conversationType === 'group' ? 'group' : 'user'] });
  dispatch('fetchUnreadMessagesCount');
}

export default {
  /** One-time setup (guarded by `state.hasInitialized`): wires up realtime socket listeners and opens the sockets. Called from App.vue's boot, globally, for the app's entire lifetime — the conversation LIST itself is no longer fetched from here (see startConversationsPolling), only real-time signal wiring, which does need to stay live app-wide (e.g. a new-message socket event while on another page). */
  initMessages({ commit, dispatch, state }) {
    if (state.hasInitialized) return;
    commit('setInitialized', true);

    // Conversation-list preview/unread updates for a live incoming message
    // now come from the ws_conversations refresh path (scheduleConversationsRefresh
    // below), which re-derives them from a real REST fetch — this socket only
    // needs to trigger a refetch of the open thread's own messages.
    // Skipped while WE have our own send in flight for this exact
    // conversation — refreshListsAfterSend already covers it, sequenced
    // after that send's own response (see sendInFlightConversationIds above).
    messagesApi.onMessagesRefresh((conversationId) => {
      if (sendInFlightConversationIds.has(conversationId)) return;
      scheduleMessagesRefresh(dispatch, conversationId);
    });

    messagesApi.onTyping(({ conversationId, isTyping }) => {
      commit('setTyping', { conversationId, isTyping });
    });

    // Skipped while we have ANY send of our own in flight — same reasoning
    // as onMessagesRefresh above, just not scoped to one conversation since
    // this signal itself isn't (see sendInFlightConversationIds' comment).
    messagesApi.onConversationsRefresh(() => {
      if (sendInFlightConversationIds.size > 0) return;
      scheduleConversationsRefresh(dispatch);
    });

    messagesApi.onPresenceChange(({ userId, presence }) => {
      commit('setParticipantPresence', { userId, presence });
    });

    // Opens the presence + conversations-list WebSockets, and watches
    // activeConversationId to open/close the per-conversation messages
    // socket — see messagesApi.js's connectRealtime for the full lifecycle.
    // Guarded internally the same way state.hasInitialized guards this
    // whole action, so it's safe even though initMessages itself is also
    // only reachable once per session.
    messagesApi.connectRealtime();
  },

  // Fetches the conversation list once, then keeps it fresh on the 20s
  // presence poll (see PRESENCE_POLL_INTERVAL_MS above) for as long as the
  // Messages section stays mounted — call from Messages/index.vue's
  // onMounted, paired with stopConversationsPolling on unmount, so the full
  // conversations/list fetch (participants, previews, membership, etc., not
  // just a lightweight unread count) only keeps firing while that section is
  // actually visible, not for the app's entire lifetime regardless of page.
  async startConversationsPolling({ dispatch }) {
    await dispatch('fetchConversations');
    if (conversationsPollTimer) return;
    conversationsPollTimer = setInterval(() => scheduleConversationsRefresh(dispatch), PRESENCE_POLL_INTERVAL_MS);
  },

  /** Stops the polling started by startConversationsPolling — call from Messages/index.vue's onUnmounted. */
  stopConversationsPolling() {
    if (!conversationsPollTimer) return;
    clearInterval(conversationsPollTimer);
    conversationsPollTimer = null;
  },

  // Loads the first page for the given search term, replacing whatever's
  // currently shown. Pass no args (or search: '') to load the unfiltered list.
  // /api/messages/conversations/list returns one homogeneous conversation_type
  // ('user' or 'group') per call, not a mixed list, so both are requested in
  // parallel by default — but each is committed to state the moment ITS OWN
  // response lands (setConversationsByType), rather than both being held
  // back behind Promise.all until the slower of the two finishes. A slow
  // 'user' request no longer blocks the group list (already back) from
  // appearing, and vice versa — each section renders as soon as its own API
  // call resolves. `types` narrows this to just one side — used by
  // refreshListsAfterSend below so sending a message only re-fetches the
  // conversation_type it actually belongs to, instead of always re-fetching
  // both regardless of which one changed.
  async fetchConversations({ commit, state }, { search = '', types = ['user', 'group'] } = {}) {
    commit('setStatus', 'loading');
    const load = (requestType) =>
      messagesApi
        .fetchConversations({ limit: state.conversationsLimit, skip: 0, search, conversationType: requestType })
        .then((page) => {
          commit('setConversationsByType', { requestType, conversations: page.conversations });
          commit('setConversationsMeta', {
            search,
            skipByType: { [requestType]: page.conversations.length },
            hasMoreByType: { [requestType]: page.hasMore },
          });
        });

    try {
      await Promise.all(types.map(load));
      commit('setStatus', 'ready');
    } catch (err) {
      commit('setError', err?.message || 'Failed to load conversations');
      commit('setStatus', 'error');
    }
  },

  // Lazy-loading (infinite scroll): appends the next page for whichever
  // conversation type(s) still have more, using the per-type skip/limit
  // already tracked in state from the last fetchConversations call.
  async fetchMoreConversations({ commit, state }) {
    const types = ['user', 'group'].filter((type) => state.hasMoreConversationsByType[type]);
    if (state.isLoadingMoreConversations || !types.length) return;
    commit('setLoadingMoreConversations', true);
    try {
      const pages = await Promise.all(
        types.map((type) =>
          messagesApi
            .fetchConversations({
              limit: state.conversationsLimit,
              skip: state.conversationsSkipByType[type],
              search: state.conversationsSearch,
              conversationType: type,
            })
            .then((page) => ({ type, ...page }))
        )
      );
      commit('appendConversations', pages.flatMap((page) => page.conversations));
      const skipByType = {};
      const hasMoreByType = {};
      for (const page of pages) {
        skipByType[page.type] = state.conversationsSkipByType[page.type] + page.conversations.length;
        hasMoreByType[page.type] = page.hasMore;
      }
      commit('setConversationsMeta', { skipByType, hasMoreByType });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchMoreConversations] failed:', err);
    } finally {
      commit('setLoadingMoreConversations', false);
    }
  },

  // First page of "Search People" results, replacing whatever's
  // currently shown. An empty search term isn't dispatched here — an empty
  // box falls back to the plain conversation list instead (see ConversationList.vue).
  async searchPeople({ commit, state }, { search = '' } = {}) {
    commit('setSearchingPeople', true);
    try {
      const { people, hasMore } = await messagesApi.searchPeople({
        limit: state.peopleSearchLimit,
        offset: 0,
        search,
      });
      commit('setPeopleSearchResults', people);
      commit('setPeopleSearchMeta', { offset: people.length, hasMore });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[searchPeople] failed:', err);
      commit('setPeopleSearchResults', []);
      commit('setPeopleSearchMeta', { offset: 0, hasMore: false });
    } finally {
      commit('setSearchingPeople', false);
    }
  },

  // Lazy-loading (infinite scroll) for the people-search results.
  async searchMorePeople({ commit, state }, { search = '' } = {}) {
    if (state.isLoadingMorePeopleSearch || !state.hasMorePeopleSearch) return;
    commit('setLoadingMorePeopleSearch', true);
    try {
      const { people, hasMore } = await messagesApi.searchPeople({
        limit: state.peopleSearchLimit,
        offset: state.peopleSearchOffset,
        search,
      });
      commit('appendPeopleSearchResults', people);
      commit('setPeopleSearchMeta', { skip: state.peopleSearchSkip + people.length, hasMore });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[searchMorePeople] failed:', err);
    } finally {
      commit('setLoadingMorePeopleSearch', false);
    }
  },

  // First page of "Add Members" results (GET /api/users, all users — not
  // just followers), replacing whatever's currently shown. Backs the group
  // member pickers (NewGroupModal, AddGroupMembersModal).
  async fetchAllUsers({ commit, state }, { search = '' } = {}) {
    commit('setSearchingAllUsers', true);
    try {
      const { people, hasMore } = await messagesApi.listAllUsers({
        limit: state.allUsersLimit,
        offset: 0,
        search,
      });
      commit('setAllUsersResults', people);
      commit('setAllUsersMeta', { offset: people.length, hasMore });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchAllUsers] failed:', err);
      commit('setAllUsersResults', []);
      commit('setAllUsersMeta', { offset: 0, hasMore: false });
    } finally {
      commit('setSearchingAllUsers', false);
    }
  },

  // Lazy-loading (infinite scroll) for the all-users results.
  async fetchMoreAllUsers({ commit, state }, { search = '' } = {}) {
    if (state.isLoadingMoreAllUsers || !state.hasMoreAllUsers) return;
    commit('setLoadingMoreAllUsers', true);
    try {
      const { people, hasMore } = await messagesApi.listAllUsers({
        limit: state.allUsersLimit,
        offset: state.allUsersOffset,
        search,
      });
      commit('appendAllUsersResults', people);
      commit('setAllUsersMeta', { offset: state.allUsersOffset + people.length, hasMore });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchMoreAllUsers] failed:', err);
    } finally {
      commit('setLoadingMoreAllUsers', false);
    }
  },

  // Opens a chat with a person picked from "Search People". Their
  // search result carries messagingChannelId — null if they have no DM
  // channel yet, in which case one is created (POST /api/messaging_channel)
  // before opening it.
  async startConversationWithPerson({ commit, dispatch, state }, person) {
    if (person.messagingChannelId) {
      if (!state.conversations.some((c) => c.id === person.messagingChannelId)) {
        commit('upsertConversation', {
          id: person.messagingChannelId,
          participant: {
            id: person.id,
            name: person.name,
            username: person.username,
            initials: person.initials,
            presence: person.presence || 'offline',
            avatarUrl: person.avatarUrl,
            verified: person.verified,
            userBadgeIcon: person.userBadgeIcon,
          },
          lastMessagePreview: '',
          lastMessageAt: Date.now(),
          unreadCount: 0,
        });
      }
      await dispatch('selectConversation', person.messagingChannelId);
      return person.messagingChannelId;
    }

    try {
      const currentUserId = messagesApi.getCurrentUser().id;
      const conversation = await messagesApi.createMessagingChannel([currentUserId, person.id]);
      commit('upsertConversation', conversation);
      // Freshly created — no message history to fetch.
      commit('setMessages', { conversationId: conversation.id, messages: [] });
      commit('setMessagesMeta', { conversationId: conversation.id, skip: 0, hasMore: false });
      commit('setActiveConversationId', conversation.id);
      return conversation.id;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[startConversationWithPerson] failed:', err);
      return null;
    }
  },

  // Creates a new group conversation (POST /api/messaging_channel,
  // conversation_type: 'group') from the "New Group Chat" modal — same
  // freshly-created-channel pattern as startConversationWithPerson above
  // (no message history to fetch, so seed an empty page directly instead of
  // round-tripping through fetchMessages).
  async createGroup({ commit }, { name, participantIds }) {
    const currentUserId = messagesApi.getCurrentUser().id;
    return createAndOpenGroup(commit, name, [currentUserId, ...participantIds]);
  },

  // Opens the group chat for one of the user's organizations (Group Chats
  // section, sourced from GET /api/organizations/my — see store/org's
  // fetchOnlyUserOrgs/getOnlyUserOrgs). The org carries its own real
  // message_channel_id (nullable — org.vue/ConversationList.vue pass it
  // through as messageChannelId), which is what actually backs the group's
  // messages, not the org's own id.
  async openGroupChatForOrg({ commit, dispatch, state }, org) {
    if (org.messageChannelId) {
      const existing = state.conversations.find((c) => c.id === org.messageChannelId);
      if (!existing) {
        commit('upsertConversation', {
          id: org.messageChannelId,
          type: 'group',
          creatorId: null,
          name: org.name,
          avatarUrl: org.avatarUrl || '',
          participants: [],
          memberCount: org.memberCount || 0,
          lastMessagePreview: '',
          lastMessageThumbnailUrl: '',
          lastMessageIsLink: false,
          lastMessagePreviewIcon: null,
          lastMessageAt: Date.now(),
          createdAt: Date.now(),
          unreadCount: 0,
        });
      }
      await dispatch('selectConversation', org.messageChannelId);
      return org.messageChannelId;
    }

    // No channel exists for this org yet — create one (POST
    // /api/messaging_channel, conversation_type: 'group', organization_id:
    // org.orgId so the backend links it back to this org) instead of calling
    // /api/messages/list or /api/messages/mark-as-read with a null id.
    try {
      const currentUserId = messagesApi.getCurrentUser().id;
      const participantIds = Array.from(new Set([currentUserId, ...(org.memberIds || [])]));
      return await createAndOpenGroup(commit, org.name, participantIds, org.orgId);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[openGroupChatForOrg] failed to create channel:', err);
      return null;
    }
  },

  /** Make a conversation active, optimistically clear its unread badge (persisting that server-side), and load its first page of messages. */
  async selectConversation({ commit, dispatch, state }, conversationId) {
    commit('setActiveConversationId', conversationId);
    // Captured before clearUnread zeroes it out, so MessageList can still
    // place its "N new messages" divider after the badge itself clears.
    const conversation = state.conversations.find((c) => c.id === conversationId);
    commit('setUnreadSnapshot', { conversationId, count: conversation?.unreadCount || 0 });
    commit('clearUnread', conversationId);

    // Fire-and-forget: the badge is already cleared optimistically above,
    // this just persists it server-side. Once it lands, refresh the
    // Leftbar's unread-messages count so it reflects the server's tally
    // rather than only the local optimistic clear. Skipped entirely when
    // there was nothing unread — no point marking an already-read
    // conversation as read on every open.
    if (conversation?.unreadCount > 0) {
      messagesApi.markConversationRead(conversationId)
        .then(() => dispatch('fetchUnreadMessagesCount'))
        .catch((err) => {
          if (import.meta.env.DEV) console.warn('[markConversationRead] failed:', err);
        });
    }

    // Only show the loading state the FIRST time this conversation is
    // opened (nothing cached yet) — re-selecting an already-visited
    // conversation (switching away and back) has its messages sitting right
    // there in state already, so flipping the loading flag on every
    // re-entry would blank the list for a beat on every switch instead of
    // just once, ever, per conversation.
    const alreadyLoaded = Boolean((state.messagesByConversation[conversationId] || []).length);
    if (!alreadyLoaded) commit('setLoadingActiveMessages', true);
    try {
      const { messages, hasMore } = await messagesApi.fetchMessages(conversationId, { limit: state.messagesLimit });
      commit('setMessages', { conversationId, messages });
      commit('setMessagesMeta', { conversationId, skip: messages.length, hasMore });
    } finally {
      if (!alreadyLoaded) commit('setLoadingActiveMessages', false);
    }
  },

  // Triggered by a ws_channels signal frame (see messagesApi.js's
  // onMessagesRefresh) — refetches the channel's newest page and merges it
  // by id (prependMessages resorts by createdAt), so an already-loaded or
  // still-optimistic message keeps its place instead of duplicating. Never
  // touches the older-messages pagination cursor — this refreshes the
  // newest page, it isn't a fetchMoreMessages-style older page. Bails if the
  // user has since switched conversations (the channel socket closes on
  // switch, but a refresh already in flight can still land after that).
  async refreshActiveMessages({ commit, dispatch, state }, conversationId) {
    if (conversationId !== state.activeConversationId) return;
    try {
      const existingIds = new Set((state.messagesByConversation[conversationId] || []).map((m) => m.id));
      const { messages } = await messagesApi.fetchMessages(conversationId, { limit: state.messagesLimit, skip: 0 });
      if (conversationId !== state.activeConversationId) return;
      commit('prependMessages', { conversationId, messages });

      // Only mark-as-read when this refresh actually surfaced a message id
      // we didn't already have — i.e. a real new message, not just an
      // updated status (delivered/seen ticks) or edit/delete on messages
      // already loaded. Marking as read on every signal, unconditionally,
      // created a feedback loop: mark-as-read very likely makes the backend
      // push another ws_channels signal (the read-state itself changed),
      // which re-triggers this action, which calls mark-as-read again,
      // forever — confirmed in the network tab as list/mark-as-read/
      // unread-count repeating every couple seconds with no real new
      // message involved.
      if (!messages.some((m) => !existingIds.has(m.id))) return;

      commit('clearUnread', conversationId);
      messagesApi.markConversationRead(conversationId)
        .then(() => dispatch('fetchUnreadMessagesCount'))
        .catch((err) => {
          if (import.meta.env.DEV) console.warn('[refreshActiveMessages] markConversationRead failed:', err);
        });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[refreshActiveMessages] failed:', err);
    }
  },

  // Fire-and-forget lookup for whether this conversation's participant
  // accepts DMs from anyone (see fetchIsAnyoneCanMessage in messagesApi.js)
  // — the participant sub-doc conversations/pendingContacts start out with
  // never carries this, only the fuller per-user profile endpoint does, so
  // it's backfilled once the thread is opened and folded into
  // useCanMessageParticipant's canMessageParticipant gate. No-op for a group
  // conversation (no `.participant`/username to look up). Dispatched from
  // MessageList.vue's getActiveConversationId watcher (alongside getMe) —
  // not from selectConversation directly — since that watcher is the one
  // point every "conversation became active" path (selectConversation,
  // startConversationWithPerson, startConversationFromContact,
  // openGroupChatForOrg) funnels through, guaranteeing this fires every
  // time regardless of which path opened the conversation.
  async fetchParticipantMessageSettings({ commit, state }, conversationId) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    const username = conversation?.participant?.username
      || state.pendingContacts[conversationId]?.participant?.username;
    if (!username) return;

    try {
      const isAnyoneCanMessage = await messagesApi.fetchIsAnyoneCanMessage(username);
      commit('setParticipantIsAnyoneCanMessage', { conversationId, isAnyoneCanMessage });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchParticipantMessageSettings] failed:', err);
    }
  },

  // Lazy-loading (infinite scroll upward): loads the next-older page of
  // messages for the given conversation and prepends them.
  async fetchMoreMessages({ commit, state }, conversationId) {
    if (state.isLoadingMoreMessagesByConversation[conversationId]) return;
    if (state.hasMoreMessagesByConversation[conversationId] === false) return;

    commit('setLoadingMoreMessages', { conversationId, value: true });
    try {
      const skip = state.messagesSkipByConversation[conversationId] || 0;
      const { messages, hasMore } = await messagesApi.fetchMessages(conversationId, {
        limit: state.messagesLimit,
        skip,
      });
      commit('prependMessages', { conversationId, messages });
      commit('setMessagesMeta', { conversationId, skip: skip + messages.length, hasMore });
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchMoreMessages] failed:', err);
    } finally {
      commit('setLoadingMoreMessages', { conversationId, value: false });
    }
  },

  /** Zero the unread badge on every conversation. */
  markAllChatsAsRead({ commit }) {
    commit('clearAllUnread');
  },

  /** Flag the given conversations as unread ("mark as unread" from the chat-list context menu). */
  markConversationsUnread({ commit }, conversationIds) {
    conversationIds.forEach((id) => commit('markUnread', id));
  },

  /** Locally clear messages for the given conversations (keeping the chat rows), optionally keeping starred messages. */
  // Empties a conversation's messages both locally and on the backend — same
  // DELETE /api/messages/delete_conversation/:id endpoint deleteConversations
  // below calls, since that's actually a per-user soft-delete (the shape
  // mapChannelParticipant's is_deleted/deleted_at comment already describes:
  // it flags THIS user's own participant record, not a global delete), not a
  // hard removal of the channel for everyone. The only difference from
  // deleteConversations is that this never touches state.conversations — the
  // chat row stays right where it is, just with an empty history, instead of
  // disappearing from the list.
  async clearConversations({ commit }, { conversationIds, keepStarred = false }) {
    conversationIds.forEach((id) => commit('clearConversationMessages', { conversationId: id, keepStarred }));

    const results = await Promise.allSettled(conversationIds.map((id) => messagesApi.deleteConversation(id)));
    const failed = results.some((result) => result.status === 'rejected');
    if (failed) {
      if (import.meta.env.DEV) console.warn('[clearConversations] failed for some chats:', results);
      showNotification('error', 'Error', 'Failed to clear some chats on the server. They may reappear on reload.', 5000);
    }
  },

  // Deletes conversations entirely (DELETE /api/messages/delete_conversation/:id)
  // — unlike clearConversations above (empties every message but keeps the
  // chat row), this removes the row itself. Optimistic, like the rest of
  // this module's delete flows; any conversation whose backend call fails is
  // put back into the list.
  async deleteConversations({ commit, state }, conversationIds) {
    const removed = state.conversations.filter((c) => conversationIds.includes(c.id));
    if (!removed.length) return;

    commit('removeConversations', conversationIds);

    const results = await Promise.allSettled(
      removed.map((conversation) => messagesApi.deleteConversation(conversation.id))
    );
    const failed = removed.filter((_, index) => results[index].status === 'rejected');

    if (failed.length) {
      if (import.meta.env.DEV) console.warn('[deleteConversations] failed for some chats, restoring:', failed);
      commit('setConversations', [...failed, ...state.conversations]);
      showNotification('error', 'Error', 'Failed to delete some chats. Please try again.', 5000);
    }
  },

  // Opens a thread for a contact/dialed number picked from "New chat" —
  // deliberately does NOT add them to the visible conversation list yet.
  // Real chat apps only show a conversation once you've actually exchanged
  // a message with someone; just opening their empty thread and switching
  // away shouldn't leave a permanent entry behind. The list entry gets
  // created for real in sendMessage, the moment the first message goes out.
  startConversationFromContact({ commit, state }, contact) {
    const conversationId = contact.conversationId || `c-contact-${contact.id}`;
    const participant = {
      id: contact.id,
      name: contact.name,
      username: contact.username,
      initials: contact.initials,
      presence: contact.presence || 'offline',
      avatarUrl: contact.avatarUrl || '',
    };

    if (!state.conversations.some((c) => c.id === conversationId)) {
      commit('setPendingContact', { conversationId, participant });
    }
    commit('setActiveConversationId', conversationId);

    if (!state.messagesByConversation[conversationId]) {
      commit('setMessages', { conversationId, messages: [] });
    }
  },

  // Accepts either a plain string (back-compat, text-only) or
  // { text, attachments, linkPreview, conversationId } for messages
  // carrying media/link previews. attachments/linkPreview are optional
  // either way. `conversationId` is optional too — omit it to send to
  // whatever's currently active; the attachment composer passes it
  // explicitly (pinned to whichever conversation it was opened for) so a
  // conversation switch while it's still open can't redirect the send.
  async sendMessage({ commit, dispatch, state }, payload) {
    const {
      text = '',
      attachments = [],
      linkPreview = null,
      conversationId: explicitConversationId = null,
      replyMessageId = null,
      replyTo = null,
    } = typeof payload === 'string' ? { text: payload } : (payload || {});

    const conversationId = explicitConversationId || state.activeConversationId;
    const trimmed = text.trim();
    if (!conversationId || (!trimmed && !attachments.length)) return;

    const tempId = `optimistic-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      conversationId,
      senderId: messagesApi.getCurrentUser().id,
      text: trimmed,
      attachments,
      linkPreview,
      replyTo,
      createdAt: Date.now(),
      status: 'sending',
    };

    // Promote a pending "New chat" contact into the real, visible list the
    // moment the first message actually goes out — not before.
    const pending = state.pendingContacts[conversationId];
    if (pending && !state.conversations.some((c) => c.id === conversationId)) {
      commit('upsertConversation', {
        id: conversationId,
        participant: pending.participant,
        lastMessagePreview: conversationPreviewText(optimisticMessage),
        lastMessageThumbnailUrl: conversationPreviewThumbnail(optimisticMessage),
        lastMessageIsLink: conversationPreviewIsLink(optimisticMessage),
        lastMessagePreviewIcon: conversationPreviewIcon(optimisticMessage),
        lastMessageAt: optimisticMessage.createdAt,
        unreadCount: 0,
      });
      commit('clearPendingContact', conversationId);
    }

    commit('addMessage', optimisticMessage);

    // Marks this conversation as having one of our own sends in flight for
    // as long as the POST below is outstanding — see
    // sendInFlightConversationIds' own comment for why (suppresses the
    // realtime-signal-triggered refresh from racing the explicit
    // refreshListsAfterSend call after this resolves).
    sendInFlightConversationIds.add(conversationId);
    try {
      // The backend has no concept of a link preview, and mapMessageResult's
      // best-guess reply mapping may not match its real field name (flagged
      // there) — keep the locally-known linkPreview/replyTo on the confirmed
      // message either way, rather than losing them once the optimistic
      // bubble is replaced.
      const confirmed = {
        ...(await messagesApi.sendMessage(conversationId, { text: trimmed, attachments, linkPreview, replyMessageId })),
        replyTo,
      };
      commit('replaceMessage', { conversationId, tempId, message: confirmed });
      commit('updateConversationPreview', {
        conversationId,
        preview: conversationPreviewText(confirmed),
        thumbnailUrl: conversationPreviewThumbnail(confirmed),
        isLink: conversationPreviewIsLink(confirmed),
        icon: conversationPreviewIcon(confirmed),
        at: confirmed.createdAt,
        incrementUnread: false,
      });
      const confirmedConversation = state.conversations.find((c) => c.id === conversationId);
      refreshListsAfterSend(dispatch, conversationId, confirmedConversation?.type);
    } catch (err) {
      // Leaving this stuck at status:'sending' forever (the old behavior)
      // looked identical to a message that was still in flight — mark it
      // failed so MessageBubble can show a retry affordance instead.
      if (import.meta.env.DEV) console.warn('[sendMessage] failed, marking message failed:', err);
      commit('replaceMessage', {
        conversationId,
        tempId,
        message: {
          id: tempId,
          conversationId,
          senderId: messagesApi.getCurrentUser().id,
          text: trimmed,
          attachments,
          linkPreview,
          replyTo,
          createdAt: Date.now(),
          status: 'failed',
        },
      });
      // err.message carries the backend's own rejection reason when present
      // (e.g. a 400 validation error) — same convention as deleteMessages
      // below, so a specific failure (not just "something went wrong")
      // reaches the user instead of a generic message every time.
      showNotification('error', 'Error', err?.message || 'Message not sent. Tap Resend to try again.', 5000);
    } finally {
      sendInFlightConversationIds.delete(conversationId);
    }
  },

  // Re-attempts sending a message that previously failed, in place —
  // same temp id, so the bubble doesn't jump position in the list.
  async retryMessage({ commit, dispatch, state }, { conversationId, messageId }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    const message = bucket.find((m) => m.id === messageId);
    if (!message || message.status !== 'failed') return;

    commit('replaceMessage', { conversationId, tempId: messageId, message: { ...message, status: 'sending' } });

    // See sendMessage's own comment on sendInFlightConversationIds above.
    sendInFlightConversationIds.add(conversationId);
    try {
      const confirmed = {
        ...(await messagesApi.sendMessage(conversationId, {
          text: message.text,
          attachments: message.attachments,
          linkPreview: message.linkPreview,
          replyMessageId: message.replyTo?.id || null,
        })),
        //linkPreview: message.linkPreview,
        replyTo: message.replyTo,
      };
      commit('replaceMessage', { conversationId, tempId: messageId, message: confirmed });
      commit('updateConversationPreview', {
        conversationId,
        preview: conversationPreviewText(confirmed),
        thumbnailUrl: conversationPreviewThumbnail(confirmed),
        isLink: conversationPreviewIsLink(confirmed),
        icon: conversationPreviewIcon(confirmed),
        at: confirmed.createdAt,
        incrementUnread: false,
      });
      const conversation = state.conversations.find((c) => c.id === conversationId);
      refreshListsAfterSend(dispatch, conversationId, conversation?.type);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[retryMessage] failed again:', err);
      commit('replaceMessage', { conversationId, tempId: messageId, message: { ...message, status: 'failed' } });
      showNotification('error', 'Error', err?.message || 'Message still not sent. Please check your connection.', 5000);
    } finally {
      sendInFlightConversationIds.delete(conversationId);
    }
  },

  // "Delete for me" (default) removes the selected messages from this
  // client's own list only, restoring them (in original order) if the
  // backend call fails. "Delete for everyone" instead replaces each message
  // in place with a deleted placeholder (WhatsApp-style "You deleted this
  // message"/"This message was deleted") — the row stays put for every
  // participant, including the deleter, matching the `is_deleted` shape a
  // fetch of this conversation's history would return afterward.
  async deleteMessages({ commit, state }, { conversationId, messageIds, forEveryone = false }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    const originals = bucket.filter((m) => messageIds.includes(m.id));
    if (!originals.length) return;

    // The conversation list's preview mirrors whatever message is last in
    // this bucket — only touch it when that's actually the one being
    // deleted, so deleting an older message in the thread doesn't clobber a
    // newer preview that's already showing.
    const wasLastMessage = messageIds.includes(bucket[bucket.length - 1].id);
    const refreshPreview = () => {
      if (!wasLastMessage) return;
      const updatedBucket = state.messagesByConversation[conversationId] || [];
      const newLast = updatedBucket[updatedBucket.length - 1];
      commit('refreshConversationPreview', {
        conversationId,
        preview: newLast ? conversationPreviewText(newLast) : '',
        thumbnailUrl: newLast ? conversationPreviewThumbnail(newLast) : '',
        isLink: newLast ? conversationPreviewIsLink(newLast) : false,
        icon: newLast ? conversationPreviewIcon(newLast) : null,
      });
    };

    if (forEveryone) {
      originals.forEach((message) => {
        commit('replaceMessage', {
          conversationId,
          tempId: message.id,
          message: { ...message, isDeleted: true, text: '', attachments: [], replyTo: null, linkPreview: null, edited: false },
        });
      });
    } else {
      commit('removeMessages', { conversationId, messageIds });
    }
    refreshPreview();

    try {
      await messagesApi.deleteMessages(conversationId, messageIds, forEveryone);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[deleteMessages] failed, restoring:', err);
      if (forEveryone) {
        originals.forEach((message) => {
          commit('replaceMessage', { conversationId, tempId: message.id, message });
        });
      } else {
        const current = state.messagesByConversation[conversationId] || [];
        const merged = [...current, ...originals].sort((a, b) => a.createdAt - b.createdAt);
        commit('setMessages', { conversationId, messages: merged });
      }
      refreshPreview();
      // err.message carries the backend's own `reason` when present (e.g.
      // "Not allowed to delete Message For Everyone" past the 15-minute
      // window — see messagesApi.js's chatApiRequest) — surface that
      // instead of a generic message so the user knows why, not just that
      // it failed.
      showNotification('error', 'Error', err?.message || 'Failed to delete message. Please try again.', 5000);
    }
  },

  // WhatsApp-style edit: optimistic text swap (flagged `edited: true` for
  // the "(edited)" label), reverted to the original on failure. Uses the
  // existing replaceMessage mutation rather than a dedicated one — editing
  // doesn't change the message's id/position, just its text.
  async editMessage({ commit, state }, { conversationId, messageId, text }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    const original = bucket.find((m) => m.id === messageId);
    if (!original) return;

    // The conversation list's preview mirrors whatever message is last in
    // this bucket — only touch it when the edited message is actually that
    // one, same guard deleteMessages uses, and refresh in place
    // (refreshConversationPreview) rather than bumping the conversation to
    // the top, since editing isn't new activity.
    const isLastMessage = bucket[bucket.length - 1]?.id === messageId;
    const refreshPreview = (message) => {
      if (!isLastMessage) return;
      commit('refreshConversationPreview', {
        conversationId,
        preview: conversationPreviewText(message),
        thumbnailUrl: conversationPreviewThumbnail(message),
        isLink: conversationPreviewIsLink(message),
        icon: conversationPreviewIcon(message),
      });
    };

    const edited = { ...original, text, edited: true };
    commit('replaceMessage', { conversationId, tempId: messageId, message: edited });
    refreshPreview(edited);

    try {
      await messagesApi.editMessage(conversationId, messageId, text);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[editMessage] failed, reverting:', err);
      commit('replaceMessage', { conversationId, tempId: messageId, message: original });
      refreshPreview(original);
      // err.message carries the backend's own rejection reason when present
      // (e.g. a 400 validation error) — same convention as deleteMessages.
      showNotification('error', 'Error', err?.message || 'Failed to save edit. Your change was not saved.', 5000);
    }
  },

  // A message that failed to send only ever existed locally (optimistic
  // tempId, never confirmed by the backend) — deleting it is a pure local
  // removal, unlike deleteMessages above, since there's no server-side
  // record to call a DELETE API against. Guarded to only ever discard a
  // message that's actually in the 'failed' state.
  discardFailedMessage({ commit, state }, { conversationId, messageId }) {
    const bucket = state.messagesByConversation[conversationId] || [];
    const message = bucket.find((m) => m.id === messageId);
    if (!message || message.status !== 'failed') return;

    commit('removeMessages', { conversationId, messageIds: [messageId] });
  },

  /** Save (or clear, if `text` is empty) the unsent composer text for a conversation. */
  saveDraft({ commit }, { conversationId, text }) {
    if (!conversationId) return;
    commit('setDraft', { conversationId, text });
  },

  /** Notify the active conversation's participants of this user's typing state via the realtime socket. */
  sendTypingStatus({ state }, isTyping) {
    if (!state.activeConversationId) return;
    messagesApi.setTypingStatus(state.activeConversationId, isTyping);
  },

  // Leftbar badge count — real endpoint (unlike the rest of this module,
  // which is still mocked). Response body is { response: <count>, status_id }.
  //
  // OLD: This hits VITE_CHAT_API (the chat backend host, distinct from the main
  // OLD: /api/ proxy target), so it falls outside the window.fetch patch in
  // OLD: main.js that auto-attaches Authorization for same-origin requests — the
  // OLD: token has to be resolved and attached here instead.
  // NOTE: now goes through the same-origin /chat-api proxy (see vite.config.js
  // and launch_pad_fe nginx config) instead of the raw VITE_CHAT_API domain, so
  // the backend URL doesn't show up in the browser's network tab. Manual auth
  // resolution kept below — harmless duplicate of what main.js's fetch patch
  // now also attaches automatically for this path.
  async fetchUnreadMessagesCount({ commit }) {
    try {
      const tokenCookie = document.cookie.split('; ').find((c) => c.startsWith('Token='))?.split('=')[1];
      const accessToken = localStorage.getItem('access_token');
      const authToken = accessToken || tokenCookie;

      // OLD: const response = await fetch(`${import.meta.env.VITE_CHAT_API}/api/messages/unread-count`, {
      const response = await fetch(`/chat-api/api/messages/unread-count`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `${authToken}` } : {}),
        },
      });
      if (response.ok) {
        const data = await response.json();
        const count = typeof data === 'object' && data !== null ? data.response : data;
        commit('setUnreadMessagesCount', Number(count) || 0);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[fetchUnreadMessagesCount] failed:', error);
    }
  },

  // "Who can DM you" — see messagesApi.js's fetchMessageSettings for why
  // this reads from localStorage rather than a real endpoint today.
  async fetchMessageSettings({ commit }) {
    try {
      const { allowMessagesFrom } = await messagesApi.fetchMessageSettings();
      commit('setDmAllowFrom', allowMessagesFrom);
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[fetchMessageSettings] failed:', err);
    }
  },

  // "Who can send messages in a group" — PATCH /api/messaging_channel.
  // Optimistic like the rest of this module's mutating actions, reverted on
  // failure. Re-fetches the conversation list on success so every other
  // derived field the backend may have recomputed alongside the toggle
  // (not just isAnyoneCanMessage itself) lands from the server's own
  // response rather than trusting the optimistic patch indefinitely.
  async updateGroupMessagePermission({ commit, dispatch, state }, { conversationId, isAnyoneCanMessage }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return false;

    const previous = conversation.isAnyoneCanMessage;
    commit('upsertConversation', { id: conversationId, isAnyoneCanMessage });
    try {
      await messagesApi.updateGroupMessagePermission(conversationId, isAnyoneCanMessage);
      dispatch('fetchConversations');
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[updateGroupMessagePermission] failed:', err);
      commit('upsertConversation', { id: conversationId, isAnyoneCanMessage: previous });
      showNotification('error', 'Error', err?.message || 'Failed to save group message settings. Please try again.', 5000);
      return false;
    }
  },

  // Uploads a new image and sets it as a group's avatar. Optimistic: shows
  // the new avatar immediately (via a local object URL) while the upload +
  // PATCH are in flight, reverts to the previous avatarUrl if either fails.
  async updateGroupAvatar({ commit, dispatch, state }, { conversationId, file }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation || !file) return false;

    const previousAvatarUrl = conversation.avatarUrl;
    const localPreviewUrl = URL.createObjectURL(file);
    commit('upsertConversation', { id: conversationId, avatarUrl: localPreviewUrl });

    try {
      const uploaded = await messagesApi.uploadGroupAvatarImage(file);
      const avatarFileId = uploaded?._id || uploaded?.id;
      await messagesApi.updateGroupAvatar(conversationId, avatarFileId);
      dispatch('fetchConversations');
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[updateGroupAvatar] failed:', err);
      commit('upsertConversation', { id: conversationId, avatarUrl: previousAvatarUrl });
      showNotification('error', 'Error', err?.message || 'Failed to update the group photo. Please try again.', 5000);
      return false;
    }
  },

  // Renames a group. Same optimistic/revert shape as updateGroupAvatar above
  // — PATCHes the same undocumented-elsewhere /api/messaging_channel
  // endpoint, just a `name` field instead (see updateGroupName's own
  // comment in messagesService.js).
  async updateGroupName({ commit, dispatch, state }, { conversationId, name }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    const trimmed = (name || '').trim();
    if (!conversation || !trimmed || trimmed === conversation.name) return false;

    const previousName = conversation.name;
    commit('upsertConversation', { id: conversationId, name: trimmed });

    try {
      // The endpoint expects isAnyoneCanMessage in the same payload as name
      // (see updateGroupName's own comment in messagesService.js) — pass the
      // conversation's current value through unchanged so renaming doesn't
      // also silently reset that separate setting.
      await messagesApi.updateGroupName(conversationId, trimmed, Boolean(conversation.isAnyoneCanMessage));
      dispatch('fetchConversations');
      showNotification('success', 'Success', 'Group name updated.', 5000);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[updateGroupName] failed:', err);
      commit('upsertConversation', { id: conversationId, name: previousName });
      showNotification('error', 'Error', err?.message || 'Failed to update the group name. Please try again.', 5000);
      return false;
    }
  },

  // Adds new members to an existing group conversation. Optimistic, like the
  // rest of this module's mutating actions: appends the selected people to
  // the local participants list immediately, then reverts if the backend
  // call fails. `members` are person records from the "Search People" flow
  // (see messagesApi.searchPeople's mapPersonResult) — remapped here into the
  // participant shape mapChannelParticipant produces so the members list
  // renders consistently with existing participants.
  async addGroupMembers({ commit, dispatch, state }, { conversationId, members }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation || !members?.length) return false;

    const previousParticipants = conversation.participants || [];
    const newParticipants = members.map((m) => ({
      id: m.id,
      name: m.name,
      username: m.username,
      initials: m.initials,
      presence: m.presence || 'offline',
      avatarUrl: m.avatarUrl,
      verified: m.verified,
      userBadgeIcon: m.userBadgeIcon,
      isAnyoneCanMessage: Boolean(m.isAnyoneCanMessage),
      role: 'member',
    }));
    const mergedParticipants = [...previousParticipants, ...newParticipants];
    commit('upsertConversation', {
      id: conversationId,
      participants: mergedParticipants,
      memberCount: mergedParticipants.length,
    });

    try {
      await Promise.all(members.map((m) => messagesApi.addGroupParticipant(conversationId, m.id)));
      dispatch('fetchConversations');
      showNotification(
        'success',
        'Success',
        members.length === 1 ? `${members[0].name} was added to the group.` : `${members.length} members were added to the group.`,
        7000
      );
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[addGroupMembers] failed:', err);
      commit('upsertConversation', {
        id: conversationId,
        participants: previousParticipants,
        memberCount: previousParticipants.length,
      });
      showNotification('error', 'Error', err?.message || 'Failed to add members. Please try again.', 5000);
      return false;
    }
  },

  // Removes the current user from a group conversation. Unlike
  // deleteConversations, exiting does NOT remove the chat from the list or
  // close the open thread — it only changes the current user's own
  // membership status in it, same as being removed by an admin
  // (removeGroupMember). Optimistically flips isRemovedFromGroup (which
  // drives the composer-disabled notice and the Exit Group/Delete
  // Conversation menu states) and re-syncs from the server, reverting the
  // flag if the API call fails, rather than yanking the conversation out of
  // view entirely.
  async exitGroup({ commit, dispatch, state }, conversationId) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return false;

    commit('upsertConversation', { id: conversationId, isRemovedFromGroup: true });

    try {
      await messagesApi.leaveGroup(conversationId);
      dispatch('fetchConversations');
      showNotification('success', 'Success', `You left "${conversation.name}".`, 7000);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[exitGroup] failed:', err);
      commit('upsertConversation', { id: conversationId, isRemovedFromGroup: false });
      showNotification('error', 'Error', err?.message || 'Failed to exit the group. Please try again.', 5000);
      return false;
    }
  },

  // Removes another participant from a group conversation (admin/owner-only
  // in the UI — see MessageThread.vue's isGroupAdmin gate on the menu item).
  // Optimistic like addGroupMembers: drops the member from the local
  // participants list immediately, reverts if the backend call fails.
  async removeGroupMember({ commit, dispatch, state }, { conversationId, memberId }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return false;

    const previousParticipants = conversation.participants || [];
    const removedMember = previousParticipants.find((p) => p.id === memberId);
    const remainingParticipants = previousParticipants.filter((p) => p.id !== memberId);
    if (remainingParticipants.length === previousParticipants.length) return false;

    commit('upsertConversation', {
      id: conversationId,
      participants: remainingParticipants,
      memberCount: remainingParticipants.length,
    });

    try {
      await messagesApi.removeGroupParticipant(conversationId, memberId);
      // Safe to re-sync from the server now — mapGroupConversationResult
      // filters out is_removed participants (that's the actual fix for the
      // member reappearing; the backend soft-deletes them — is_removed:
      // true, removed_at set — rather than dropping them from
      // result.participant[] outright).
      dispatch('fetchConversations');
      showNotification('success', 'Success', `${removedMember?.name || 'Member'} was removed from the group.`, 7000);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[removeGroupMember] failed:', err);
      commit('upsertConversation', {
        id: conversationId,
        participants: previousParticipants,
        memberCount: previousParticipants.length,
      });
      showNotification('error', 'Error', err?.message || 'Failed to remove member. Please try again.', 5000);
      return false;
    }
  },

  // Promotes another participant to admin (Make Group Admin,
  // GroupMembersModal only — gated behind isGroupAdmin there, same as
  // addGroupMembers/removeGroupMember). Optimistic: patches the local role
  // immediately, reverts if the backend call fails.
  async makeGroupMemberAdmin({ commit, dispatch, state }, { conversationId, memberId }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return false;

    const previousParticipants = conversation.participants || [];
    const promotedMember = previousParticipants.find((p) => p.id === memberId);
    const updatedParticipants = previousParticipants.map((p) =>
      p.id === memberId ? { ...p, role: 'admin' } : p
    );

    commit('upsertConversation', { id: conversationId, participants: updatedParticipants });

    try {
      await messagesApi.updateParticipantRole(conversationId, memberId, 'admin');
      dispatch('fetchConversations');
      showNotification('success', 'Success', `${promotedMember?.name || 'Member'} is now a group admin.`, 7000);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[makeGroupMemberAdmin] failed:', err);
      commit('upsertConversation', { id: conversationId, participants: previousParticipants });
      showNotification('error', 'Error', err?.message || 'Failed to update role. Please try again.', 5000);
      return false;
    }
  },

  // Reverse of makeGroupMemberAdmin above — demotes an admin back to a
  // regular member (Make Group Member, GroupMembersModal only, same
  // isGroupAdmin gate). Same optimistic patch/revert shape, just 'member'
  // instead of 'admin' sent to updateParticipantRole.
  async makeGroupMemberRegular({ commit, dispatch, state }, { conversationId, memberId }) {
    const conversation = state.conversations.find((c) => c.id === conversationId);
    if (!conversation) return false;

    const previousParticipants = conversation.participants || [];
    const demotedMember = previousParticipants.find((p) => p.id === memberId);
    const updatedParticipants = previousParticipants.map((p) =>
      p.id === memberId ? { ...p, role: 'member' } : p
    );

    commit('upsertConversation', { id: conversationId, participants: updatedParticipants });

    try {
      await messagesApi.updateParticipantRole(conversationId, memberId, 'member');
      dispatch('fetchConversations');
      showNotification('success', 'Success', `${demotedMember?.name || 'Member'} is no longer a group admin.`, 7000);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[makeGroupMemberRegular] failed:', err);
      commit('upsertConversation', { id: conversationId, participants: previousParticipants });
      showNotification('error', 'Error', err?.message || 'Failed to update role. Please try again.', 5000);
      return false;
    }
  },

  /** Save the "who can DM you" preference, optimistically applying it locally; returns whether the save succeeded. */
  async updateMessageSettings({ commit }, allowMessagesFrom) {
    commit('setDmSettingsSaving', true);
    try {
      await messagesApi.updateMessageSettings(allowMessagesFrom);
      commit('setDmAllowFrom', allowMessagesFrom);
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[updateMessageSettings] failed:', err);
      showNotification('error', 'Error', 'Failed to save message settings. Please try again.', 5000);
      return false;
    } finally {
      commit('setDmSettingsSaving', false);
    }
  },
};
